import type { ReactNode } from 'react'

import { createContext, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import type { UserInfo } from '@/lib/types'
import { isSteam } from '@/lib/platform'
import { steamAutoAuth } from '@/lib/steam'
import {
  returnTargetNavigation,
  signInUrlReturningTo,
} from '@/lib/return-target'
import { webPushGameReturnTargetFromLocation } from '@/lib/notification-destination'

export const AuthContext = createContext<{
  userInfo: UserInfo | null
  authResolved: boolean
  browserSessionId: string | null
}>({
  userInfo: null,
  authResolved: false,
  browserSessionId: null,
})

AuthContext.displayName = 'AuthContext'

export function AuthProvider({
  children,
  userInfo,
  authResolved,
  browserSessionId,
}: {
  children: ReactNode
  userInfo: UserInfo | null
  authResolved: boolean
  browserSessionId: string | null
}) {
  const { pathname, search, hash } = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const registeredUser = userInfo && !userInfo.isAnonymous
    const notificationReturnTarget = webPushGameReturnTargetFromLocation(
      pathname,
      search,
    )

    if (registeredUser) {
      if (pathname === '/signin') {
        const redirect = new URLSearchParams(search).get('redirect')
        const destination = returnTargetNavigation(
          redirect,
          '/dashboard',
          window.location.pathname,
        )
        if (destination.fullNavigation)
          window.location.replace(destination.to)
        else navigate(destination.to, { replace: true })
      }
      return
    }

    if (isSteam) {
      void steamAutoAuth()
      return
    }

    if (
      (pathname.startsWith('/dashboard') || notificationReturnTarget !== null)
      && (!userInfo || userInfo.isAnonymous)
    ) {
      navigate(
        signInUrlReturningTo(
          notificationReturnTarget ?? `${pathname}${search}${hash}`,
        ),
        { replace: true },
      )
    }
  }, [pathname, search, hash, userInfo, navigate])

  const value = useMemo(
    () => ({ userInfo, authResolved, browserSessionId }),
    [userInfo, authResolved, browserSessionId],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
